import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { IconButton, Badge, Tooltip, Box, Alert, Button, AlertTitle, Grow } from "@mui/material";
import { useAtom, useAtomValue } from "jotai";
import dayjs from "dayjs";
import { textColorState, notificationsListState, ortexColorState } from "@/lib/store";
import { notificationsViewed } from "@/controllers/notifications";
import SettingsIcon from "@mui/icons-material/Settings";
import styles from "./Notifications.module.scss";

// Opened from the bell menu: its dialog code loads on the first open, not with the header on every page.
const NotificationSettingsDialog = dynamic(() => import("./NotificationSettingsDialog"), { ssr: false });

let viewedNotifications: NodeJS.Timeout;
let readNotifications: NodeJS.Timeout;

const animationTime = 3500;
const animationCooldown = 15000;

export default function Notifications() {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [notificationsList, setNotificationsList] = useAtom(notificationsListState);
    const [showReadNotifications, setShowReadNotifications] = useState(false);
    const [iconAnimation, setIconAnimation] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    // Mounted from the first open on, so its open and close transitions are unchanged.
    const settingsOpened = useRef(false);
    if (settingsOpen) settingsOpened.current = true;
    const ortexColor = useAtomValue(ortexColorState);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);

        const currentNotifications = notificationsList.filter((n) => !n.viewed);

        currentNotifications.map((n) => {
            notificationsViewed(n.notification_id).then(() => {
                viewedNotifications = setTimeout(() => {
                    setNotificationsList((notificationsList) =>
                        notificationsList.map((n) => {
                            return { ...n, viewed: true };
                        })
                    );
                }, 300);
            });
        });
    };

    const open = Boolean(anchorEl);
    const popoverId = open ? "notifications-popover" : undefined;

    useEffect(() => {
        if (!open) {
            readNotifications = setTimeout(() => {
                setShowReadNotifications(false);
            }, 300);
        }

        return () => {
            clearTimeout(readNotifications);
        };
    }, [open]);

    useEffect(() => {
        let timeoutId;

        const checkUnreadNotifications = () => {
            const unreadPriorityNotifications = notificationsList.filter((n) => !n.viewed && n.notification_priority === "1").length;
            if (unreadPriorityNotifications) {
                setIconAnimation(true);
                timeoutId = setTimeout(() => {
                    setIconAnimation(false);
                    timeoutId = setTimeout(() => {
                        checkUnreadNotifications();
                    }, animationCooldown);
                }, animationTime * 3);
            } else {
                setIconAnimation(false);
            }
        };

        checkUnreadNotifications();

        return () => {
            clearTimeout(timeoutId);
        };
    }, [notificationsList]);

    const textColor = useAtomValue(textColorState);

    const IconColor = (alertType: string) => {
        switch (alertType) {
            case "Info":
                return "info";
            case "Warning":
                return "warning";
            case "Alert":
                return "error";
            case "Payment":
                return "success";
        }
    };

    const unread = notificationsList.filter((n) => !n.viewed).length;

    return (
        <div className={styles.notifications_wrapper}>
            <Tooltip
                arrow
                title="Notifications"
                slotProps={{
                    popper: {
                        modifiers: [
                            {
                                name: "offset",
                                options: {
                                    offset: [0, +6],
                                },
                            },
                        ],
                    },
                }}
            >
                {/* The name carries the count as shown (MUI caps it at 99+). A hidden badge is only scaled to zero, so its
                    text ("0", or the last count) was still inside the button and did not match the name
                    (label-content-name-mismatch): once its exit animation is over it is visibility: hidden, and the badge is
                    aria-hidden because the name already says it. */}
                <IconButton aria-label={unread ? `Notifications, ${unread > 99 ? "99+" : unread} new` : "Notifications"} aria-describedby={popoverId} onClick={handleClick} size="small">
                    <Badge
                        badgeContent={unread}
                        slotProps={{ badge: { "aria-hidden": true } }}
                        sx={{ "& .MuiBadge-invisible": { visibility: "hidden", transition: "transform 195ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear 195ms" } }}
                        color={notificationsList.filter((n) => n?.notification_priority === "1").length ? "error" : "primary"}
                        className={iconAnimation ? styles.badgeAnimation : null}
                    >
                        {/* Bootstrap Icons "bell-fill", inline: importing it from react-icons/bs put the whole icon pack (~147 KB) in the shared bundle. */}
                        <svg
                            stroke="currentColor"
                            fill="currentColor"
                            strokeWidth="0"
                            viewBox="0 0 16 16"
                            height="1em"
                            width="1em"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ color: textColor }}
                            className={iconAnimation ? styles.bellAnimation : null}
                        >
                            <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zm.995-14.901a1 1 0 1 0-1.99 0A5.002 5.002 0 0 0 3 6c0 1.098-.5 6-2 7h14c-1.5-1-2-5.902-2-7 0-2.42-1.72-4.44-4.005-4.901z" />
                        </svg>
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                id={popoverId}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <Box className={styles.popupContent} sx={{ backgroundColor: "transparent" }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            variant="text"
                            size="small"
                            startIcon={<SettingsIcon fontSize="small" />}
                            sx={{ color: textColor, "&:hover": { color: ortexColor } }}
                            onClick={() => {
                                handleClose();
                                setSettingsOpen(true);
                            }}
                        >
                            <Typography variant="overline">Notification settings</Typography>
                        </Button>
                    </Box>
                    {showReadNotifications
                        ? notificationsList
                              .filter((n) => n.viewed)
                              .sort((a, b) => (Number(a.notification_priority) > Number(b.notification_priority) ? 1 : -1))
                              .map((notification, i) => (
                                  <Grow in={open} key={notification.notification_id} style={{ transformOrigin: "0 0 0" }} {...(open ? { timeout: 500 * (i + 1) } : {})}>
                                      <div style={{ marginTop: 5, marginBottom: 5 }}>
                                          <Alert variant="standard" severity={IconColor(notification.notification_type)}>
                                              <div className={styles.notification_date}>
                                                  <Typography variant="subtitle2">{dayjs(notification.notification_start_time).format("DD/MM/YYYY")}</Typography>
                                              </div>
                                              <AlertTitle>
                                                  <Typography variant="h2">{notification.notification_title}</Typography>
                                              </AlertTitle>
                                              <Typography variant="body2">{notification.notification_message}</Typography>
                                          </Alert>
                                      </div>
                                  </Grow>
                              ))
                        : notificationsList
                              .filter((n) => !n.viewed)
                              .sort((a, b) => (Number(a.notification_priority) > Number(b.notification_priority) ? 1 : -1))
                              .map((notification, i) => (
                                  <Grow in={open} key={notification.notification_id} style={{ transformOrigin: "0 0 0" }} {...(open ? { timeout: 500 * (i + 1) } : {})}>
                                      <div style={{ marginTop: 5, marginBottom: 5 }}>
                                          <Alert variant="standard" severity={IconColor(notification.notification_type)}>
                                              <div className={styles.notification_date}>
                                                  <Typography variant="caption">{dayjs(notification.notification_start_time).format("DD/MM/YYYY")}</Typography>
                                              </div>
                                              <AlertTitle>
                                                  <Typography variant="h2">{notification.notification_title}</Typography>
                                              </AlertTitle>
                                              <Typography variant="body2">{notification.notification_message}</Typography>
                                          </Alert>
                                      </div>
                                  </Grow>
                              ))}
                    {!showReadNotifications && !notificationsList.filter((n) => !n.viewed).length && (
                        <Alert severity="success" sx={{ minWidth: "200px" }}>
                            No new notifications
                        </Alert>
                    )}
                    {showReadNotifications && !notificationsList.filter((n) => n.viewed).length && (
                        <Alert severity="success" sx={{ minWidth: "200px" }}>
                            No read notifications
                        </Alert>
                    )}
                    <Button
                        variant="text"
                        size="small"
                        className={styles.readNotificationsButton}
                        sx={[
                            {
                                color: textColor,
                                "&:hover": {
                                    color: ortexColor,
                                },
                            },
                        ]}
                        // sx={{ color: textColor }}
                        onClick={() => setShowReadNotifications(!showReadNotifications)}
                    >
                        <Typography variant="overline">{showReadNotifications ? "Hide" : "See"} read notifications</Typography>
                    </Button>
                </Box>

                {/* <Divider sx={{ backgroundColor: dividerColor }} /> */}
            </Popover>
            {settingsOpened.current && <NotificationSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />}
        </div>
    );
}
